import {
  type RequisiteIssue,
  RU_TIME_ZONES,
  type RuTimeZone,
  validateBik,
  validateCorrespondentAccount,
  validateInn,
  validateKpp,
  validateOgrn,
  validateSettlementAccount,
} from '@glory/kernel';

/**
 * Как студия называет себя клиенту — бренд, как на сайте (кабинет, письма кабинета, шапка КП и документов). Юридическое
 * название («ИП …») — только в реквизитах и подписях.
 */
export const STUDIO_BRAND_NAME = 'Глори.Цифра';

/** Часовые пояса России: студия считает сроки и даты писем по своему поясу. */
export const STUDIO_TIMEZONES = RU_TIME_ZONES;
export type StudioTimezone = RuTimeZone;

/**
 * Форма: индивидуальный предприниматель или организация (ООО, АО). От неё зависят длина ИНН
 * и ОГРН, КПП (у ИП его нет) и подписант (у ИП — сам предприниматель).
 */
export const LEGAL_FORMS = ['individual', 'legal'] as const;
export type LegalForm = (typeof LEGAL_FORMS)[number];

/** Реквизиты студии: всё, что идёт в договоры, акты, счета и подписи писем. */
export interface StudioRequisites {
  readonly legalForm: LegalForm;
  readonly fullName: string | null;
  readonly shortName: string | null;
  readonly inn: string | null;
  readonly kpp: string | null;
  readonly ogrn: string | null;
  readonly legalAddress: string | null;
  readonly bankBik: string | null;
  readonly bankName: string | null;
  readonly bankCorrespondentAccount: string | null;
  readonly bankAccount: string | null;
  readonly signerName: string | null;
  readonly signerPosition: string | null;
  /** «Устава», «доверенности № 12 от 01.09.2026». */
  readonly signerBasis: string | null;
  readonly email: string | null;
  /** E.164: +79991234567. */
  readonly phone: string | null;
  readonly website: string | null;
  readonly timezone: StudioTimezone;
  /** Фирменный цвет `#RRGGBB`: клиентская страница и PDF КП. */
  readonly brandColor: string;
}

/** Красный студии по умолчанию (раздел 17 спецификации подпроекта 3). */
export const DEFAULT_BRAND_COLOR = '#E30613';

export const isBrandColor = (value: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(value);

export const EMPTY_STUDIO: StudioRequisites = {
  // Студия пока работает как ИП; переход в ООО — смена формы и реквизитов.
  legalForm: 'individual',
  fullName: null,
  shortName: null,
  inn: null,
  kpp: null,
  ogrn: null,
  legalAddress: null,
  bankBik: null,
  bankName: null,
  bankCorrespondentAccount: null,
  bankAccount: null,
  signerName: null,
  signerPosition: null,
  signerBasis: null,
  email: null,
  phone: null,
  website: null,
  timezone: 'Europe/Moscow',
  brandColor: DEFAULT_BRAND_COLOR,
};

export type StudioField = keyof StudioRequisites;

/** Номер верный, но не той формы: 12-значный ИНН у организации и т. п. */
export type LegalFormIssue =
  'inn_not_individual' | 'inn_not_legal' | 'ogrn_not_individual' | 'ogrn_not_legal';

export interface StudioIssue {
  readonly field: StudioField;
  readonly issue: RequisiteIssue | LegalFormIssue | 'brand_color';
}

const text = (value: string | null): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
};
const compact = (value: string | null): string | null => text(value?.replace(/\s/g, '') ?? null);

/** Как ввели → как храним: без пробелов по краям и внутри номеров, пустое — null. */
export function normalizeStudio(input: StudioRequisites): StudioRequisites {
  return {
    legalForm: input.legalForm,
    fullName: text(input.fullName),
    shortName: text(input.shortName),
    inn: compact(input.inn),
    // У ИП нет КПП: если остался от прежней формы — убираем.
    kpp: input.legalForm === 'individual' ? null : (compact(input.kpp)?.toUpperCase() ?? null),
    ogrn: compact(input.ogrn),
    legalAddress: text(input.legalAddress),
    bankBik: compact(input.bankBik),
    bankName: text(input.bankName),
    bankCorrespondentAccount: compact(input.bankCorrespondentAccount),
    bankAccount: compact(input.bankAccount),
    signerName: text(input.signerName),
    signerPosition: text(input.signerPosition),
    signerBasis: text(input.signerBasis),
    email: text(input.email)?.toLowerCase() ?? null,
    phone: compact(input.phone),
    website: text(input.website),
    timezone: input.timezone,
    // Цвет хранится заглавными: `#e30613` и `#E30613` — один и тот же.
    brandColor: text(input.brandColor)?.toUpperCase() ?? DEFAULT_BRAND_COLOR,
  };
}

/**
 * Что не так с реквизитами (уже нормализованными). Пустые поля допустимы — реквизиты
 * заполняются постепенно; заполненные проверяются по формату и контрольным суммам.
 */
export function studioIssues(input: StudioRequisites): StudioIssue[] {
  const issues: StudioIssue[] = [];
  const check = (
    field: StudioField,
    value: string | null,
    validate: (v: string) => StudioIssue['issue'] | null,
  ) => {
    if (value === null) return;
    const issue = validate(value);
    if (issue) issues.push({ field, issue });
  };
  if (!isBrandColor(input.brandColor)) issues.push({ field: 'brandColor', issue: 'brand_color' });
  const individual = input.legalForm === 'individual';
  check('inn', input.inn, (inn) => {
    const issue = validateInn(inn);
    if (issue) return issue;
    if (individual && inn.length !== 12) return 'inn_not_individual';
    if (!individual && inn.length !== 10) return 'inn_not_legal';
    return null;
  });
  check('kpp', input.kpp, validateKpp);
  check('ogrn', input.ogrn, (ogrn) => {
    const issue = validateOgrn(ogrn);
    if (issue) return issue;
    if (individual && ogrn.length !== 15) return 'ogrn_not_individual';
    if (!individual && ogrn.length !== 13) return 'ogrn_not_legal';
    return null;
  });
  check('bankBik', input.bankBik, validateBik);
  const bikValid = input.bankBik !== null && validateBik(input.bankBik) === null;
  const bik = input.bankBik ?? '';
  check('bankCorrespondentAccount', input.bankCorrespondentAccount, (account) =>
    bikValid
      ? validateCorrespondentAccount(account, bik)
      : /^\d{20}$/.test(account)
        ? null
        : 'account_format',
  );
  check('bankAccount', input.bankAccount, (account) =>
    bikValid ? validateSettlementAccount(account, bik) : /^\d{20}$/.test(account) ? null : 'account_format',
  );
  return issues;
}

/** Шаг «Реквизиты» чек-листа запуска выполнен: есть название и ИНН. */
export function studioFilled(input: StudioRequisites): boolean {
  return input.shortName !== null && input.inn !== null;
}
